import { Component, inject, Injectable } from '@angular/core';
import { CodeSnippet } from './code-snippet.interface';
import { Observable, Subject, lastValueFrom } from 'rxjs';
import { ScriptMode } from '../enums';

import  Mustache from 'mustache';
import { DevExtremeDataSourceAdapter } from '@tools';
import { Config } from '@config';
import { UrlEnhancerService } from '@url';
import { States, StateService } from '@state';

@Component({
    selector: 'add-root-cause-snippet',
    template: `
        <clx-popover
            #editorwindow
            *ngIf="isDialogOpen"
            [minWidth]="400"
            [maxWidth]="800"
            class="popup"
            height="auto"
            width="auto"
            title="Select Root Causes"
            i18n-title="@@script-editor.snippet.select-root-cause.popup.title"
            [closeOnOutsideClick]="false"
            [(visible)]="isDialogOpen"
            (visibleChange)="visibilityChanged($event)"
        >
            <clx-form
                [save]="addRootCause.bind(this)"
                messageTarget="errormessage"
                >
                <messages
                    name="errormessage"
                    >
                </messages>
                <div
                    class="error message"
                    *ngIf="error"
                    i18n="@@script-editor.snippet.popup.message.plant-missing"
                >Please choose a plant/system before adding a datapoint</div>
                <clx-row has="full-size-columns big-layout">
                    <clx-label>Variable name</clx-label>
                    <clx-item>
                        <clx-input
                            name="varname"
                            value="alarm"
                        >
                            <clx-validation type="required"></clx-validation>
                        </clx-input>

                    </clx-item>
                </clx-row>
                <clx-row has="full-size-columns big-layout">
                    <clx-label>Root Causes</clx-label>
                    <clx-multiselect [keepOrder]=true emptyText="Choose Root Causes" name="rootCauses">
                        <clx-value *ngFor="let entry of data" [text]="entry.label" [value]="entry"></clx-value>
					    <clx-validation type="required"></clx-validation>
                    </clx-multiselect>
                </clx-row>
                <clx-row has="full-size-columns big-layout">
                    <clx-label>Priority</clx-label>
                    <clx-item>
                        <clx-select type name="priority">
                            <clx-value text="Undefined" value="-1"></clx-value>
                            <clx-value text="Low" value="3"></clx-value>
                            <clx-value text="Medium" value="2"></clx-value>
                            <clx-value text="High" value="1"></clx-value>
                        </clx-select>
                    </clx-item>
                </clx-row>
                <clx-row has="full-size-columns big-layout">
                    <clx-label>Reset Type</clx-label>
                    <clx-item>
                        <clx-select name="resetType">
                            <clx-value text="-- select an option --" value=""></clx-value>
                            <clx-value text="Manual" value="MANUAL"></clx-value>
                            <clx-value text="Auto" value="AUTO"></clx-value>
                            <clx-value text="Manual or Auto" value="MANUAL OR AUTO"></clx-value>
                        </clx-select>
                    </clx-item>
                </clx-row>
                <clx-row type="buttons">
                    <clx-button
                        type="save"
                        i18n="@@script-editor.snippets.add-root-causes.button"
                    >Add</clx-button>
                </clx-row>
            </clx-form>
        </clx-popover>
    
    `,
    standalone: false
})
export class AddRootCausesSnippet implements CodeSnippet{

    #dxDataAdapter: DevExtremeDataSourceAdapter = inject(DevExtremeDataSourceAdapter);

    #config: Config = inject(Config);

    #urlEnhancer: UrlEnhancerService = inject(UrlEnhancerService);

    #stateService: StateService = inject(StateService);

    async #getRootCauses() {
        const subTenantIds = this.#stateService.value(States.selectedSubtenants).get() || [];
        let url = this.#urlEnhancer.replaceParams(
            this.#config.services.alarm.rootCausesList,
        );
        if(subTenantIds?.length) {
            url += encodeURIComponent(subTenantIds.join(','));
        }
        
        return this.#dxDataAdapter.getData(url);
    }

    visibilityChanged(value) {
        if(value === false) {
            this.subject.next(null);
            this.isDialogOpen = false;
            this.subject.complete();
        }
    }

    part = true;

    text: string = $localize`:@@script-editor.snippet.root-causes:Root Causes`;

    static category: string = 'climatix';

    isDialogOpen: boolean = false;

    data: any;

    selectedRootCauses: any;

    subject: Subject<any> = new Subject()

    static filter = [
        ScriptMode.alarmAction
    ];

    observable: Observable<any> = this.subject.asObservable();

    addRootCause(data) {
        this.subject.next(data);//{ data.selectedRootCause, data.priority, data.resetType, data.varname })
        this.subject.complete()
    }

    async insert(): Promise<string> {
        this.subject = new Subject()
        this.observable = this.subject.asObservable();
        const causes = await this.#getRootCauses();
        this.data = causes.items;
        this.isDialogOpen = true;
        const value = await lastValueFrom(this.observable);
        this.isDialogOpen = false;

        if (value.rootCauses.length < 1)
            return "";

        let frmt = `
            -- attach rootcauses and/or override priority or resetType to alarmtype
            {{varname}}.addRootCause(`;

        if (value.rootCauses.length == 1)
            frmt += `

                {{#rootCauses}}-- single rootcause - RootCause: '{{label}}'
                "{{alarmDashboardRootCauseId}}"{{/rootCauses}}`;
        else {
            if (value.rootCauses.length > 1)
                value.rootCauses[value.rootCauses.length - 1].last = true;
            frmt += `

                -- multiple rootcauses
                {
                {{#rootCauses}}
                    "{{alarmDashboardRootCauseId}}"{{^last}},{{/last}}{{#last}} {{/last}} -- RootCause: '{{label}}'
                {{/rootCauses}}
                }`;
        }

        if (value.priority != -1)
            frmt += `,

                -- priority override
                {{priority}}`;

        if (value.resetType != "")
            frmt += `,

                -- resetType override
                "{{resetType}}"`;

        frmt += `
            )
            `;

        return Mustache.render(frmt, value);
    }

}